import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { DropdownButton, Dropdown } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import setting4Icon from 'assets/images/icons/setting-4.svg';
import tableIconsIcon from 'assets/images/icons/table-icons.svg';
import issuesGreyIcon from 'assets/images/icons/issues-grey.svg';
import chevronGreyIcon from 'assets/images/icons/chevron-grey.svg';
import NestedSelect from 'components/form-components/NestedSelect';
import gripVerticalIcon from 'assets/images/icons/grip-vertical.svg';
import FormButton from 'components/form-components/FormButton';
import { useLocation, useNavigate } from 'react-router-dom';
import DashboardAddWidgetModal from './DashboardAddWidgetModal/DashboardAddWidgetModal';

const GRID = { COLUMNS: 12, ROWS: 6 };
const DEFAULT_COL_SPAN = 2;
const DEFAULT_ROW_SPAN = 2;
const STORAGE_KEY = 'dashboard_widget_positions';
const GAP = 15;

// Helper functions
const getWidgetDimensions = (widget) => ({
  colSpan: widget.colSpan ?? DEFAULT_COL_SPAN,
  rowSpan: widget.rowSpan ?? DEFAULT_ROW_SPAN,
});

const calculateMaxRowWithWidgets = (widgets) => {
  if (widgets.length === 0) return GRID.ROWS;
  return Math.max(
    ...widgets.map((w) => {
      const { rowSpan } = getWidgetDimensions(w);
      return w.row + rowSpan - 1;
    }),
  );
};

const isOverlapping = (w1Col, w1Row, w1ColSpan, w1RowSpan, w2Col, w2Row, w2ColSpan, w2RowSpan) =>
  w1Col < w2Col + w2ColSpan &&
  w1Col + w1ColSpan > w2Col &&
  w1Row < w2Row + w2RowSpan &&
  w1Row + w1RowSpan > w2Row;

// Grid utilities
const grid = {
  isOccupied: (widgets, col, row, colSpan, rowSpan, excludeId) =>
    widgets.some((w) => {
      if (w.id === excludeId) return false;
      const { colSpan: wColSpan, rowSpan: wRowSpan } = getWidgetDimensions(w);
      return isOverlapping(col, row, colSpan, rowSpan, w.column, w.row, wColSpan, wRowSpan);
    }),

  isValid: (widgets, col, row, colSpan, rowSpan, excludeId, totalRows = GRID.ROWS) =>
    col >= 1 &&
    row >= 1 &&
    col + colSpan <= GRID.COLUMNS + 1 &&
    row + rowSpan <= totalRows + 1 &&
    !grid.isOccupied(widgets, col, row, colSpan, rowSpan, excludeId),

  findSpace: (widgets, widgetId, totalRows = GRID.ROWS) => {
    const w = widgets.find((w) => w.id === widgetId);
    if (!w) return null;

    const { colSpan, rowSpan } = getWidgetDimensions(w);
    const overlapsOriginal = (col, row) =>
      isOverlapping(col, row, colSpan, rowSpan, w.column, w.row, colSpan, rowSpan);

    // Check right side in same row
    for (let col = w.column + colSpan; col <= GRID.COLUMNS - colSpan + 1; col++) {
      if (
        !overlapsOriginal(col, w.row) &&
        grid.isValid(widgets, col, w.row, colSpan, rowSpan, widgetId, totalRows)
      ) {
        return { column: col, row: w.row };
      }
    }

    // Check next row
    const nextRow = w.row + rowSpan;
    if (nextRow + rowSpan <= totalRows + 1) {
      for (let col = 1; col <= GRID.COLUMNS - colSpan + 1; col++) {
        if (
          !overlapsOriginal(col, nextRow) &&
          grid.isValid(widgets, col, nextRow, colSpan, rowSpan, widgetId, totalRows)
        ) {
          return { column: col, row: nextRow };
        }
      }
    }

    // Scan all positions
    for (let row = 1; row <= totalRows - rowSpan + 1; row++) {
      for (let col = 1; col <= GRID.COLUMNS - colSpan + 1; col++) {
        if (
          !overlapsOriginal(col, row) &&
          grid.isValid(widgets, col, row, colSpan, rowSpan, widgetId, totalRows)
        ) {
          return { column: col, row };
        }
      }
    }
    return null;
  },

  findAvailableSpace: (widgets, colSpan, rowSpan, totalRows = GRID.ROWS) => {
    // Scan all positions to find first available space
    for (let row = 1; row <= totalRows - rowSpan + 1; row++) {
      for (let col = 1; col <= GRID.COLUMNS - colSpan + 1; col++) {
        if (grid.isValid(widgets, col, row, colSpan, rowSpan, null, totalRows)) {
          return { column: col, row };
        }
      }
    }
    return null;
  },

  positionFromMouse: (
    e,
    rect,
    colSpan,
    rowSpan,
    totalRows = GRID.ROWS,
    allowBeyondRows = false,
  ) => {
    if (!rect) return null;
    const cellW = (rect.width - GAP * (GRID.COLUMNS - 1)) / GRID.COLUMNS;
    const cellH = (rect.height - GAP * (totalRows - 1)) / totalRows;

    const col = Math.max(
      1,
      Math.min(
        Math.floor((e.clientX - rect.left + GAP / 2) / (cellW + GAP)) + 1,
        GRID.COLUMNS - colSpan + 1,
      ),
    );

    // Allow calculating rows beyond totalRows during drag
    const calculatedRow = Math.floor((e.clientY - rect.top + GAP / 2) / (cellH + GAP)) + 1;
    const row = allowBeyondRows
      ? Math.max(1, calculatedRow)
      : Math.max(1, Math.min(calculatedRow, totalRows - rowSpan + 1));

    return { column: col, row };
  },

  canMove: (widgets, w, dir, totalRows = GRID.ROWS, allowBeyondRows = false) => {
    const { colSpan, rowSpan } = getWidgetDimensions(w);
    const deltas = { left: [-1, 0], right: [1, 0], up: [0, -1], down: [0, 1] };
    const [dCol, dRow] = deltas[dir];
    const newCol = w.column + dCol;
    const newRow = w.row + dRow;

    // For down movement, allow moving beyond totalRows if allowBeyondRows is true
    const rowCheck =
      allowBeyondRows && dir === 'down'
        ? newRow >= 1
        : newRow >= 1 && newRow + rowSpan <= totalRows + 1;

    return (
      newCol >= 1 &&
      rowCheck &&
      newCol + colSpan <= GRID.COLUMNS + 1 &&
      grid.isValid(
        widgets,
        newCol,
        newRow,
        colSpan,
        rowSpan,
        w.id,
        allowBeyondRows ? Math.max(totalRows, newRow + rowSpan) : totalRows,
      )
    );
  },
};

// Storage helper
const storage = {
  load: () => {
    try {
      const data = localStorage.getItem(STORAGE_KEY);
      return data
        ? JSON.parse(data).map((w) => ({
            ...w,
            colSpan: w.colSpan ?? DEFAULT_COL_SPAN,
            rowSpan: w.rowSpan ?? DEFAULT_ROW_SPAN,
          }))
        : [
            { id: 1, column: 1, row: 1, colSpan: DEFAULT_COL_SPAN, rowSpan: DEFAULT_ROW_SPAN },
            // { id: 2, column: 4, row: 1, colSpan: 6, rowSpan: 3 },
          ];
    } catch {
      return [
        { id: 1, column: 1, row: 1, colSpan: DEFAULT_COL_SPAN, rowSpan: DEFAULT_ROW_SPAN },
        // { id: 2, column: 4, row: 1, colSpan: 6, rowSpan: 3 },
      ];
    }
  },
  save: (widgets) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(widgets));
    } catch (e) {
      console.error('Save failed:', e);
    }
  },
};

const DashboardWidgets = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [widgets, setWidgets] = useState(storage.load);
  const [pageId] = useState(location.state?.pageId ?? '');
  const [openMenus, setOpenMenus] = useState({});
  const [dragState, setDragState] = useState({ widgetId: null, position: null });
  const [isEditMode, setIsEditMode] = useState(false);
  const [totalRows, setTotalRows] = useState(() => {
    // Calculate initial total rows based on existing widgets
    const loaded = storage.load();
    if (loaded.length === 0) return GRID.ROWS;
    const maxRow = Math.max(
      ...loaded.map((w) => {
        const { rowSpan } = getWidgetDimensions(w);
        return w.row + rowSpan - 1;
      }),
    );
    return Math.max(GRID.ROWS, maxRow);
  });
  const [showNewWidgetModal, setShowNewWidgetModal] = useState(false);

  const gridRef = useRef(null);
  const widgetsRef = useRef(widgets);
  const dragStateRef = useRef(dragState);
  const widgetRefs = useRef({});

  // Sync refs
  useEffect(() => {
    widgetsRef.current = widgets;
    storage.save(widgets);
  }, [widgets]);

  useEffect(() => {
    dragStateRef.current = dragState;
  }, [dragState]);

  // Recalculate totalRows based on actual widget positions (ensures we don't keep extra rows when moving up)
  useEffect(() => {
    if (dragState.widgetId) return; // Don't recalculate during drag

    if (widgets.length === 0) {
      setTotalRows(GRID.ROWS);
      return;
    }

    const maxRow = Math.max(
      ...widgets.map((w) => {
        const { rowSpan } = getWidgetDimensions(w);
        return w.row + rowSpan - 1;
      }),
    );

    // Set totalRows to max row + 1 extra row, but at least GRID.ROWS
    const newTotalRows = Math.max(GRID.ROWS, maxRow + 1);

    // Only update if different to avoid unnecessary re-renders
    setTotalRows((prev) => (prev !== newTotalRows ? newTotalRows : prev));
  }, [widgets, dragState.widgetId]);

  const getDropdownDir = useCallback((widgetId) => {
    const el = widgetRefs.current[widgetId];
    if (!el) return 'end';
    const rect = el.getBoundingClientRect();
    return window.innerWidth - rect.right < 134 && rect.left >= 134 ? 'start' : 'end';
  }, []);

  const moveWidget = useCallback(
    (id, dir) => {
      setWidgets((prev) => {
        const widget = prev.find((w) => w.id === id);
        if (!widget) return prev;

        // Check if move is valid (allow beyond rows for down movement)
        if (!grid.canMove(prev, widget, dir, totalRows, true)) return prev;

        const deltas = { left: [-1, 0], right: [1, 0], up: [0, -1], down: [0, 1] };
        const [dCol, dRow] = deltas[dir];
        const newCol = widget.column + dCol;
        const newRow = widget.row + dRow;
        const { rowSpan } = getWidgetDimensions(widget);

        // If moving down beyond current rows, expand totalRows
        if (dir === 'down' && newRow + rowSpan > totalRows + 1) {
          setTotalRows(newRow + rowSpan); // +1 extra row is handled by the recalculation effect
        }

        return prev.map((w) => {
          if (w.id !== id) return w;
          return { ...w, column: newCol, row: newRow };
        });
      });
    },
    [totalRows],
  );

  const duplicateWidget = useCallback((id) => {
    setWidgets((prev) => {
      const widget = prev.find((w) => w.id === id);
      if (!widget) return prev;

      const { colSpan, rowSpan } = getWidgetDimensions(widget);

      // Calculate actual max row with widgets to search within the real grid space
      const maxRow = calculateMaxRowWithWidgets(prev);
      const searchLimit = Math.max(GRID.ROWS, maxRow + 1); // maxRow + 1 extra row

      // First, try to find available space in existing grid
      let pos = grid.findAvailableSpace(prev, colSpan, rowSpan, searchLimit);

      // If no space found, create new rows below
      if (!pos) {
        const newRowStart = maxRow;
        setTotalRows((prevRows) => Math.max(prevRows, newRowStart + rowSpan));
        pos = { column: 1, row: newRowStart };
      }

      return [
        ...prev,
        {
          id: Math.max(...prev.map((w) => w.id), 0) + 1,
          column: pos.column,
          row: pos.row,
          colSpan,
          rowSpan,
        },
      ];
    });
  }, []);

  const deleteWidget = useCallback((id) => {
    setWidgets((prev) => prev.filter((w) => w.id !== id));
  }, []);

  const addWidget = useCallback((colSpan, rowSpan) => {
    setWidgets((prev) => {
      // Calculate actual max row with widgets to search within the real grid space
      const maxRow = calculateMaxRowWithWidgets(prev);
      const searchLimit = Math.max(GRID.ROWS, maxRow + 1); // maxRow + 1 extra row

      // First, try to find available space in existing grid
      let position = grid.findAvailableSpace(prev, colSpan, rowSpan, searchLimit);

      // If no space found, create new rows below
      if (!position) {
        const newRowStart = maxRow;
        setTotalRows((prevRows) => Math.max(prevRows, newRowStart + rowSpan));
        position = { column: 1, row: newRowStart };
      }

      const newId = Math.max(...prev.map((w) => w.id), 0) + 1;
      return [
        ...prev,
        {
          id: newId,
          column: position.column,
          row: position.row,
          colSpan,
          rowSpan,
        },
      ];
    });
  }, []);

  const handleDragMove = useCallback(
    (e) => {
      const { widgetId } = dragStateRef.current;
      if (!widgetId) return;

      const widget = widgetsRef.current.find((w) => w.id === widgetId);
      if (!widget) return;

      const { colSpan, rowSpan } = getWidgetDimensions(widget);
      const pos = grid.positionFromMouse(
        e,
        gridRef.current?.getBoundingClientRect(),
        colSpan,
        rowSpan,
        totalRows,
        true, // Allow beyond rows during drag
      );

      if (!pos || (pos.column === widget.column && pos.row === widget.row)) {
        setDragState((prev) => ({ ...prev, position: null }));
        return;
      }

      // Check if position requires expanding totalRows
      const requiredRows = pos.row + rowSpan - 1;
      let currentTotalRows = totalRows;
      if (requiredRows > totalRows) {
        // Automatically expand totalRows to accommodate the new position + 1 extra row
        const newTotalRows = requiredRows + 1;
        setTotalRows(newTotalRows);
        currentTotalRows = newTotalRows;
      }

      // Validate position with potentially expanded totalRows
      const isValid = grid.isValid(
        widgetsRef.current,
        pos.column,
        pos.row,
        colSpan,
        rowSpan,
        widgetId,
        currentTotalRows,
      );
      setDragState((prev) => ({ ...prev, position: isValid ? pos : null }));
    },
    [totalRows],
  );

  const handleDragEnd = useCallback(() => {
    const { widgetId, position } = dragStateRef.current;

    if (widgetId && position) {
      const widget = widgetsRef.current.find((w) => w.id === widgetId);
      if (widget) {
        const { rowSpan } = getWidgetDimensions(widget);
        const requiredRows = position.row + rowSpan - 1;

        // Ensure totalRows is expanded if needed (with 1 extra row)
        setTotalRows((prevRows) => {
          if (requiredRows + 1 > prevRows) {
            return requiredRows + 1;
          }
          return prevRows;
        });

        setWidgets((prev) => prev.map((w) => (w.id === widgetId ? { ...w, ...position } : w)));
      }
    }

    setDragState({ widgetId: null, position: null });
    document.removeEventListener('mousemove', handleDragMove);
    document.removeEventListener('mouseup', handleDragEnd);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, [handleDragMove]);

  // const handleDragStart = useCallback(
  //   (e, widgetId) => {
  //     if (!isEditMode) return;
  //     e.preventDefault();
  //     e.stopPropagation();
  //     setDragState({ widgetId, position: null });
  //     document.addEventListener('mousemove', handleDragMove);
  //     document.addEventListener('mouseup', handleDragEnd);
  //     document.body.style.cursor = 'grabbing';
  //     document.body.style.userSelect = 'none';
  //   },
  //   [handleDragMove, handleDragEnd, isEditMode],
  // );

  const getMenuItems = useCallback(
    (w) => [
      {
        label: 'Move to up',
        enabled: grid.canMove(widgets, w, 'up', totalRows),
        action: () => moveWidget(w.id, 'up'),
      },
      {
        label: 'Move to down',
        enabled: grid.canMove(widgets, w, 'down', totalRows, true), // Allow beyond rows
        action: () => moveWidget(w.id, 'down'),
      },
      {
        label: 'Move to left',
        enabled: grid.canMove(widgets, w, 'left', totalRows),
        action: () => moveWidget(w.id, 'left'),
      },
      {
        label: 'Move to right',
        enabled: grid.canMove(widgets, w, 'right', totalRows),
        action: () => moveWidget(w.id, 'right'),
      },
      { label: 'Duplicate', enabled: true, action: () => duplicateWidget(w.id) },
      { label: 'Delete', enabled: true, isDelete: true, action: () => deleteWidget(w.id) },
    ],
    [moveWidget, duplicateWidget, deleteWidget, widgets, totalRows],
  );

  // Calculate the maximum row that has a widget
  const maxRowWithWidget = useMemo(() => {
    if (widgets.length === 0) return GRID.ROWS;
    const maxRow = Math.max(
      ...widgets.map((w) => {
        const { rowSpan } = getWidgetDimensions(w);
        return w.row + rowSpan - 1;
      }),
    );
    return Math.max(GRID.ROWS, maxRow);
  }, [widgets]);

  // Always show 1 extra row beyond the last widget to ensure space for new widgets
  // Also account for drag positions that might be beyond current widgets
  const displayRows = useMemo(() => {
    let rows = maxRowWithWidget + 1;

    // If dragging, ensure displayRows includes the drag position + 1 extra row
    if (dragState.widgetId && dragState.position) {
      const draggedWidget = widgets.find((w) => w.id === dragState.widgetId);
      if (draggedWidget) {
        const { rowSpan } = getWidgetDimensions(draggedWidget);
        const dragEndRow = dragState.position.row + rowSpan - 1;
        rows = Math.max(rows, dragEndRow + 1);
      }
    }

    // Use the maximum of calculated rows and totalRows (totalRows may have expanded)
    return Math.max(rows, totalRows);
  }, [maxRowWithWidget, dragState, widgets, totalRows]);

  const visibleWidgets = useMemo(
    () => widgets.filter((w) => w.row <= displayRows),
    [widgets, displayRows],
  );

  const emptyBoxes = useMemo(() => {
    const occupied = new Set();
    widgets.forEach((w) => {
      // Skip the dragged widget - it's not in its original position during drag
      if (dragState.widgetId && w.id === dragState.widgetId) {
        return;
      }
      const { colSpan, rowSpan } = getWidgetDimensions(w);
      for (let col = w.column; col < w.column + colSpan; col++) {
        for (let row = w.row; row < w.row + rowSpan; row++) {
          occupied.add(`${col}-${row}`);
        }
      }
    });

    return Array.from({ length: GRID.COLUMNS * displayRows }, (_, i) => {
      const row = Math.floor(i / GRID.COLUMNS) + 1;
      const col = (i % GRID.COLUMNS) + 1;
      return !occupied.has(`${col}-${row}`) ? { index: i + 1, col, row } : null;
    }).filter(Boolean);
  }, [widgets, displayRows, dragState]);

  const dropZone = useMemo(() => {
    if (!dragState.position || !dragState.widgetId) return null;
    const widget = widgets.find((w) => w.id === dragState.widgetId);
    if (!widget) return null;
    const { colSpan, rowSpan } = getWidgetDimensions(widget);

    // Show drop zone even if position is beyond displayRows (will expand grid)
    return (
      <div
        key='drop-zone'
        className='widget-drop-zone'
        style={{
          gridColumn: `${dragState.position.column} / span ${colSpan}`,
          gridRow: `${dragState.position.row} / span ${rowSpan}`,
        }}
      />
    );
  }, [dragState, widgets]);

  const widgetOptions = useMemo(() => {
    const baseOptions = [
      {
        value: '2x2',
        displayValue: 'Widget 2 X 2',
        colSpan: 2,
        rowSpan: 2,
      },
      {
        value: '4x6',
        displayValue: 'Widget 4 X 6',
        rowSpan: 4,
        colSpan: 6,
      },
      {
        value: '2x4',
        displayValue: 'Widget 2 X 4',
        rowSpan: 2,
        colSpan: 4,
      },
    ];

    // All options are always enabled since we can create new rows
    return baseOptions.map((option) => ({
      ...option,
      disabled: false,
    }));
  }, []);

  const handleWidgetSelect = useCallback(
    (name, value) => {
      const option = widgetOptions.find((opt) => opt.value === value);
      if (option) {
        addWidget(option.colSpan, option.rowSpan);
      }
    },
    [widgetOptions, addWidget],
  );

  const handleSave = useCallback(() => {
    storage.save(widgets);
    setIsEditMode(false);
  }, [widgets]);

  const handleEdit = useCallback(() => {
    setIsEditMode(true);
  }, []);

  const selectToggleComponent = (
    <>
      <label className='cursor-pointer'>{t('select_select')}</label>
      <img src={chevronGreyIcon} className='rotate-180' />
    </>
  );

  return (
    <div className='p-0 w-100'>
      <div className='mb-2 d-flex justify-content-end align-items-center gap-2'>
        {isEditMode ? (
          <>
            <div className='widget-select-container'>
              <NestedSelect
                toggleComponent={selectToggleComponent}
                options={widgetOptions}
                name='widget-select'
                handleChange={handleWidgetSelect}
                toggleClasses='d-flex justify-content-between align-items-center px-2 py-2 border-grey-1-h-1 gap-1 '
                fullWidth
              />
            </div>
            <button className='btn btn-primary px-3 py-2' onClick={handleSave}>
              Save
            </button>
          </>
        ) : (
          <>
            <FormButton
              text='Add new widget'
              variant='white-1'
              classes='mt-3'
              onClick={() => setShowNewWidgetModal(true)}
            />
            <button className='btn btn-primary px-3 py-2' onClick={handleEdit}>
              Edit
            </button>
            <button
              className='btn btn-primary px-3 py-2'
              onClick={() => {
                navigate('/dashboard/analytics1', { state: { pageId } });
              }}
            >
              View
            </button>
          </>
        )}
      </div>
      <div
        className='dashboard-widgets-grid'
        ref={gridRef}
        style={{
          minHeight: 'calc(100vh - 120px)',
          gridTemplateRows: `repeat(${displayRows}, minmax(0, 1fr))`,
        }}
      >
        {visibleWidgets.map((widget) => {
          const isDragging = dragState.widgetId === widget.id;
          const pos = isDragging ? dragState.position : null;
          const { colSpan, rowSpan } = getWidgetDimensions(widget);

          return (
            <div
              key={widget.id}
              ref={(el) => el && (widgetRefs.current[widget.id] = el)}
              className={`bg-white rounded shadow-sm d-flex flex-column h-100 widget-card ${
                isDragging ? 'dragging' : ''
              }`}
              style={{
                gridColumn: `${pos?.column ?? widget.column} / span ${colSpan}`,
                gridRow: `${pos?.row ?? widget.row} / span ${rowSpan}`,
                opacity: isDragging ? 0.5 : 1,
              }}
            >
              <div className='d-flex flex-column h-100'>
                {isEditMode && (
                  <>
                    <div className='d-flex justify-content-between align-items-center px-3 py-2 flex-shrink-0'>
                      <div
                        className='grip-vertical-wrapper'
                        // onMouseDown={(e) => handleDragStart(e, widget.id)}
                      >
                        <img
                          src={gripVerticalIcon}
                          alt='grip'
                          className='grip-vertical'
                          draggable={false}
                        />
                      </div>
                      <div
                        className={`d-flex gap-0 align-items-center dropdown-wrapper dropdown-${getDropdownDir(
                          widget.id,
                        )}`}
                      >
                        <img src={setting4Icon} alt='settings' className='setting-icon-4' />
                        <DropdownButton
                          show={openMenus[widget.id] || false}
                          onToggle={() =>
                            setOpenMenus((prev) => ({ ...prev, [widget.id]: !prev[widget.id] }))
                          }
                          drop={getDropdownDir(widget.id)}
                          variant='link'
                          className='p-0 border-0 bg-transparent'
                          autoClose
                          title={
                            <img
                              src={tableIconsIcon}
                              alt='menu'
                              className='table-icons cursor-pointer'
                            />
                          }
                        >
                          {getMenuItems(widget).map((item, idx) => (
                            <Dropdown.Item
                              key={idx}
                              onClick={item.enabled ? item.action : undefined}
                              disabled={!item.enabled}
                              className={`${!item.enabled ? 'text-muted' : ''} ${
                                item.isDelete ? 'text-danger' : ''
                              }`}
                            >
                              {item.label}
                            </Dropdown.Item>
                          ))}
                        </DropdownButton>
                      </div>
                    </div>
                    <hr className='my-0 flex-shrink-0' />
                  </>
                )}
                <div className='d-flex flex-column justify-content-center align-items-center flex-grow-1 px-3'>
                  <div className='small text-center theme-text-grey-1 mb-2'>No of submissions</div>
                  <div className='display-6 fw-bold text-center theme-text-black-1 mb-3'>1345</div>
                  <div className='d-flex justify-content-center align-items-center mt-2'>
                    <img src={issuesGreyIcon} alt='info' className='issues-grey' />
                  </div>
                </div>
              </div>
            </div>
          );
        })}

        {dropZone}

        {emptyBoxes.map((box) => (
          <div
            key={box.index}
            className={`dashboard-widget-box d-flex align-items-center justify-content-center ${
              !isEditMode ? 'dashboard-widget-box-hidden' : ''
            }`}
            style={{
              gridColumn: `${box.col} / span 1`,
              gridRow: `${box.row} / span 1`,
            }}
          />
        ))}
      </div>

      {showNewWidgetModal && (
        <DashboardAddWidgetModal
          editMode={false}
          handleClose={() => setShowNewWidgetModal(false)}
          pageId={'695cb88824d235f2d540dafe'}
          dashboardId={'695cb88724d235f2d540dafc'}
        />
      )}
    </div>
  );
};

export default DashboardWidgets;
